import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

interface ChildRecord {
  uid: string;
  case_number: string;
  full_name: string;
  age?: number;
  gender?: string;
  incident_type?: string;
  case_status?: string;
  priority_level?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

export default function SWChatbot() {
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState<ChildRecord[]>([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [question, setQuestion] = useState('');

  const [messages, setMessages] =
    useState<ChatMessage[]>([
      {
        role: 'assistant',
        text:
          'Hello Social Worker 👋\n\nI am SW Assistant, the DCPCMS AI Case Assistant.\n\nSelect an assigned case and ask me anything about the authorized case records.',
      },
    ]);

  const [loading, setLoading] = useState(false);
  const [loadingChildren, setLoadingChildren] =
    useState(false);

  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !user?.uid) {
      return;
    }

    loadChildren();
  }, [open, user?.uid]);

  const loadChildren = async () => {
    if (!user?.uid) {
      setError('Social Worker UID is not available.');
      return;
    }

    setLoadingChildren(true);
    setError('');

    try {
      const res = await fetch(
        `https://guardiancloudcare.com/backend/api/chatbot/get_children.php?uid=${encodeURIComponent(
          user.uid
        )}`
      );

      const text = await res.text();

      console.log('CHILDREN RESPONSE:');
      console.log(text);

      let data: any;

      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(
          'Invalid response from the chatbot case API.'
        );
      }

      if (!res.ok || !data.success) {
        throw new Error(
          data.message ||
            'Unable to load assigned cases.'
        );
      }

      setChildren(
        Array.isArray(data.children)
          ? data.children
          : []
      );
    } catch (err) {
      console.error(err);

      setChildren([]);

      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load assigned cases.'
      );
    } finally {
      setLoadingChildren(false);
    }
  };

  const cleanAssistantAnswer = (text: string) => {
    if (!text) return '';

    return text
      // Remove bold markdown: **text**
      .replace(/\*\*(.*?)\*\*/gs, '$1')
      // Remove remaining double asterisks
      .replace(/\*\*/g, '')
      .trim();
  };

  const sendQuestion = async () => {
    if (
      !user?.uid ||
      !selectedCase ||
      !question.trim() ||
      loading
    ) {
      return;
    }

    const userQuestion = question.trim();

    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        text: userQuestion,
      },
    ]);

    setQuestion('');
    setLoading(true);
    setError('');

    try {
      const res = await fetch(
        'https://guardiancloudcare.com/backend/api/chatbot/chatbot.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: user.uid,
            case_number: selectedCase,
            question: userQuestion,
          }),
        }
      );

      const text = await res.text();

      console.log('SERVER RESPONSE:');
      console.log(text);

      let data: any;

      try {
        data = JSON.parse(text);
      } catch {
        console.error(
          'Invalid JSON:',
          text
        );

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text:
              'The chatbot server returned an invalid response.\n\n' +
              text.substring(0, 500),
          },
        ]);

        return;
      }

      console.log(data);

      if (data.success) {
        const cleanedAnswer = cleanAssistantAnswer(
          data.answer ||
            'I cannot find that information in the authorized DCPCMS records.'
        );

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: cleanedAnswer,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text:
              data.message ||
              'Unable to process the request.',
          },
        ]);
      }
    } catch (err) {
      console.error(err);

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text:
            'Unable to contact the chatbot server.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const selectedChild =
    children.find(
      (child) =>
        child.case_number ===
        selectedCase
    );

  const clearConversation = () => {
    setMessages([
      {
        role: 'assistant',
        text:
          'Hello Social Worker 👋\n\nI am SW Assistant, the DCPCMS AI Case Assistant.\n\nSelect an assigned case and ask me anything about the authorized case records.',
      },
    ]);

    setQuestion('');
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] w-16 h-16 rounded-full bg-[#081257] text-white shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center group"
          aria-label="Open SW Assistant"
        >
          <i className="ri-robot-2-fill text-3xl" />

          <span className="absolute right-20 whitespace-nowrap bg-[#081257] text-white px-4 py-2 rounded-xl opacity-0 group-hover:opacity-100 transition pointer-events-none">
            SW Assistant
          </span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-[9999] w-[380px] max-w-[calc(100vw-2rem)] h-[620px] max-h-[calc(100vh-2rem)] bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
          <div className="bg-[#081257] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                <i className="ri-robot-2-fill text-2xl" />
              </div>

              <div>
                <h2 className="font-bold text-lg">
                  SW Assistant
                </h2>

                <p className="text-xs text-blue-100">
                  AI Case Assistant
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={
                  clearConversation
                }
                className="w-10 h-10 rounded-xl hover:bg-white/10"
                title="Clear conversation"
              >
                <i className="ri-refresh-line text-xl" />
              </button>

              <button
                type="button"
                onClick={() =>
                  setOpen(false)
                }
                className="w-10 h-10 rounded-xl hover:bg-white/10"
                title="Close"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
          </div>

          <div className="px-4 pt-4 bg-white border-b border-gray-100">
            <label className="text-xs font-semibold text-gray-500">
              Assigned Case
            </label>

            <select
              value={selectedCase}
              onChange={(e) =>
                setSelectedCase(
                  e.target.value
                )
              }
              disabled={
                loadingChildren
              }
              className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-700 outline-none focus:border-[#081257]"
            >
              <option value="">
                {loadingChildren
                  ? 'Loading assigned cases...'
                  : 'Select an assigned case'}
              </option>

              {children.map(
                (child) => (
                  <option
                    key={`${child.uid}-${child.case_number}`}
                    value={
                      child.case_number
                    }
                  >
                    {child.full_name} —{' '}
                    {
                      child.case_number
                    }
                  </option>
                )
              )}
            </select>

            {selectedChild && (
              <div className="mt-3 mb-4 rounded-xl bg-blue-50 border border-blue-100 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-blue-700">
                      {
                        selectedChild.full_name
                      }
                    </p>

                    <p className="text-[11px] text-blue-600 mt-1">
                      {
                        selectedChild.incident_type ||
                        'Case'
                      }
                      {' · '}
                      {
                        selectedChild.case_status ||
                        'Status unavailable'
                      }
                    </p>
                  </div>

                  <span className="text-[10px] font-semibold bg-white text-blue-700 px-2 py-1 rounded-lg">
                    Authorized
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-xs px-3 py-2">
                {error}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-4">
            {messages.map(
              (msg, index) => (
                <div
                  key={index}
                  className={`flex ${
                    msg.role === 'user'
                      ? 'justify-end'
                      : 'justify-start'
                  }`}
                >
                  <div
                    className={`rounded-2xl px-4 py-3 max-w-[86%] whitespace-pre-line shadow-sm text-sm ${
                      msg.role === 'user'
                        ? 'bg-[#081257] text-white'
                        : 'bg-white text-gray-700 border border-gray-100 text-justify'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              )
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm text-sm text-gray-500">
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    SW Assistant is processing...
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 p-4 bg-white">
            {!selectedCase && (
              <p className="text-[11px] text-gray-400 mb-2">
                Select an assigned case before asking a question.
              </p>
            )}

            <div className="flex items-center gap-3">
              <input
                value={question}
                onChange={(e) =>
                  setQuestion(
                    e.target.value
                  )
                }
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter'
                  ) {
                    e.preventDefault();
                    sendQuestion();
                  }
                }}
                type="text"
                maxLength={2000}
                disabled={
                  !selectedCase ||
                  loading
                }
                placeholder={
                  selectedCase
                    ? 'Ask about this case...'
                    : 'Select a case first...'
                }
                className="flex-1 rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-[#081257] disabled:bg-gray-100 disabled:text-gray-400"
              />

              <button
                type="button"
                onClick={
                  sendQuestion
                }
                disabled={
                  !selectedCase ||
                  !question.trim() ||
                  loading
                }
                className="w-12 h-12 rounded-2xl bg-[#081257] text-white hover:bg-[#13237c] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                aria-label="Send question"
              >
                <i className="ri-send-plane-fill" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}